import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, Routes, Route, Navigate } from 'react-router-dom';
import {
  fetchFields,
  fetchFieldsByObject,
  fetchSyncConfigs,
  fetchSyncStatus,
  fetchFullSyncStatus,
  triggerFieldSync,
  setSelectedObject,
  setSearchQuery,
  runQuery,
  setQueryText,
  clearQueryResult,
  clearActiveSyncWorkflow,
  resetInitialLoad,
  pollSyncWorkflowProgress,
} from '../store/slices/analyticsSlice';
import { generateTraceId, logUserAction } from '../utils/trace';
import { redirectToSalesforceReauth, redirectToHubSpotReauth } from '../api/auth';
import { sanitizeDisplayError } from '../utils/sanitizeDisplayError';
import * as analyticsApi from '../api/analytics';
import SOQLEditor from '../components/query/SOQLEditor';
import FrankenQLBuilder from '../components/query/FrankenQLBuilder';
import QueryTranslatorNotes from '../components/query/QueryTranslatorNotes';
import ViewsTab from '../components/analytics/ViewsTab';
import VisualizationsTab from '../components/analytics/VisualizationsTab';
import DashboardsTab from '../components/analytics/DashboardsTab';
import DependencyGraphTab from '../components/analytics/DependencyGraphTab';
import DataSourceSelector from '../components/DataSourceSelector';
import { getDataSourceConfig } from '../components/DataSourcesTab';
import { fetchDataSources } from '../store/slices/dataSourcesSlice';

function QueryContent({ editorMetadata, builderObjects }) {
  const dispatch = useDispatch();
  const {
    queryText,
    queryColumns,
    queryRows,
    queryError,
    isRunningQuery,
    queryHistory,
  } = useSelector((state) => state.analytics);
  const { selectedDataSource } = useSelector((state) => state.dataSources);
  const [showBuilder, setShowBuilder] = useState(false);
  const editorRef = useRef(null);

  const handleRunQuery = () => {
    if (queryText.trim()) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'run_query', { queryLength: queryText.length });
      dispatch(runQuery({
        query: queryText,
        traceId,
        dataSourceId: selectedDataSource?.data_source_id || null,
      }));
    }
  };

  return (
    <div className="space-y-4">
      {/* Query Editor */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-bold text-gray-900">SOQL/SQL Query</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowBuilder(b => !b)}
              className={`px-3 py-1.5 rounded-lg transition text-sm border ${
                showBuilder
                  ? 'bg-primary-50 text-primary-700 border-primary-300'
                  : 'text-gray-600 border-gray-200 hover:bg-gray-100'
              }`}
            >
              {showBuilder ? 'Hide Builder' : 'Builder'}
            </button>
            <button
              onClick={() => dispatch(clearQueryResult())}
              className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition text-sm"
              disabled={isRunningQuery}
            >
              Clear
            </button>
            <button
              onClick={handleRunQuery}
              disabled={isRunningQuery || !queryText.trim()}
              className="px-4 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRunningQuery ? 'Running...' : 'Run Query'}
            </button>
          </div>
        </div>
        {showBuilder && (
          <div className="border-b border-gray-200">
            <FrankenQLBuilder
              objects={builderObjects}
              dataSourceId={selectedDataSource?.data_source_id ?? null}
              initialSourceId={selectedDataSource?.data_source_id ?? null}
              onQueryGenerated={(sql) => {
                if (editorRef.current?.isEmpty()) {
                  dispatch(setQueryText(sql));
                } else {
                  editorRef.current?.insertAtCursor('\n' + sql);
                }
              }}
            />
          </div>
        )}
        <div className="p-4">
          <SOQLEditor
            ref={editorRef}
            value={queryText}
            onChange={(value) => dispatch(setQueryText(value))}
            onExecute={handleRunQuery}
            metadata={editorMetadata}
            height="200px"
            readOnly={isRunningQuery}
          />
          <p className="mt-2 text-xs text-gray-500">
            Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-700">Ctrl+Enter</kbd> to run. Use <code className="px-1 py-0.5 bg-gray-100 rounded font-mono">sf.Account</code>, <code className="px-1 py-0.5 bg-gray-100 rounded font-mono">hs.contacts</code> syntax to query across data sources.
          </p>
          <QueryTranslatorNotes />
        </div>
      </div>

      {/* Query Error */}
      {queryError && (() => {
        const errorCode = queryError?.error || queryError?.error_code;
        const rawErrorMsg = queryError?.message || (typeof queryError === 'string' ? queryError : 'Query failed');
        const errorMsg = sanitizeDisplayError(rawErrorMsg, 'Query failed');

        if (errorCode === 'schema_stale') {
          // Extract missing column name from DuckDB error message, e.g.:
          // "Binder Error: Referenced column \"Description\" not found in FROM clause!"
          const colMatch = errorMsg.match(/column[^"]*"([^"]+)"/i) || errorMsg.match(/column\s+(\w+)/i);
          const missingCol = colMatch ? colMatch[1] : null;

          const removeAndRun = () => {
            if (!missingCol) return;
            // Remove the column from SELECT clause and re-run.
            // Handles: "SELECT Id, Description, Name" → "SELECT Id, Name"
            const cleaned = queryText
              .replace(new RegExp(`\\b${missingCol}\\b\\s*,\\s*`, 'i'), '')   // col followed by comma
              .replace(new RegExp(`,\\s*\\b${missingCol}\\b`, 'i'), '')        // comma before col
              .replace(new RegExp(`\\bSELECT\\s+${missingCol}\\b`, 'i'), 'SELECT *'); // only col → *
            dispatch(setQueryText(cleaned));
            const traceId = generateTraceId();
            dispatch(runQuery({ query: cleaned, traceId, dataSourceId: selectedDataSource?.data_source_id || null }));
          };

          return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-medium text-amber-800">Field not synced</h3>
                  <p className="mt-1 text-sm text-amber-700">
                    {missingCol
                      ? <><strong>{missingCol}</strong> is in your query but hasn&apos;t been synced yet.</>
                      : 'One or more fields in your query haven\'t been synced yet.'}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => {
                        // Navigate to sync config tab to add the field
                        window.location.hash = '#/analytics/sync';
                      }}
                      className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition"
                    >
                      Add to sync config
                    </button>
                    {missingCol && (
                      <button
                        onClick={removeAndRun}
                        className="px-3 py-1.5 border border-amber-400 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 transition"
                      >
                        Remove &ldquo;{missingCol}&rdquo; and run
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-medium text-red-800">Query Error</h3>
                <pre className="mt-1 text-sm text-red-700 whitespace-pre-wrap font-mono">{errorMsg}</pre>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Query Results */}
      {queryColumns.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-bold text-gray-900">Results</h2>
            <span className="text-sm text-gray-500">
              {queryRows.length} row{queryRows.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {queryColumns.map((col, idx) => (
                    <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {queryRows.slice(0, 100).map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-gray-50">
                    {queryColumns.map((col, colIdx) => (
                      <td key={colIdx} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {row[col] !== null && row[col] !== undefined ? String(row[col]) : <span className="text-gray-400 italic">null</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {queryRows.length > 100 && (
              <div className="p-4 text-center text-sm text-gray-500 bg-gray-50">
                Showing 100 of {queryRows.length} rows
              </div>
            )}
          </div>
        </div>
      )}

      {/* Query History */}
      {queryHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900">Recent Queries</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {queryHistory.map((item, idx) => (
              <button
                key={idx}
                onClick={() => dispatch(setQueryText(item.query))}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition"
              >
                <code className="text-sm text-gray-700 font-mono block truncate">{item.query}</code>
                <span className="text-xs text-gray-400 mt-1 block">
                  {new Date(item.timestamp).toLocaleString()}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function AnalyticsPage() {
  const dispatch = useDispatch();
  const { activeTenant, isLoading: isAuthLoading } = useSelector((state) => state.auth);
  const isAdmin = activeTenant?.role === 'admin';
  const {
    objects,
    selectedObject,
    objectFields,
    isTriggeringSync,
    initialLoadComplete,
    initialLoadStatus,
    activeSyncWorkflow,
    error,
    authByDataSource,
  } = useSelector((state) => state.analytics);

  // Get selected data source for filtering
  const { selectedDataSource, items: dataSources } = useSelector((state) => state.dataSources);

  // Detect available data source types
  const dataSourceTypes = useMemo(() => {
    if (!dataSources || dataSources.length === 0) return [];
    return [...new Set(dataSources.map(ds => ds.source_type))];
  }, [dataSources]);

  const hasSalesforce = dataSourceTypes.includes('salesforce');
  const hasHubSpot = dataSourceTypes.includes('hubspot');

  // Generate dynamic splash page text based on available sources
  const splashPageText = useMemo(() => {
    if (hasSalesforce && hasHubSpot) {
      return {
        title: 'Welcome to Field Analytics',
        description: 'Analyze your data sources, find orphaned fields, and identify dependencies across your metadata.',
        connectionType: 'data sources'
      };
    } else if (hasHubSpot) {
      return {
        title: 'Welcome to Field Analytics',
        description: 'Analyze your HubSpot data, find unused properties, and identify dependencies across your metadata.',
        connectionType: 'HubSpot'
      };
    } else if (hasSalesforce) {
      return {
        title: 'Welcome to Field Analytics',
        description: 'Analyze your Salesforce org\'s field usage, find orphaned fields, and identify dependencies across your metadata.',
        connectionType: 'Salesforce'
      };
    } else {
      return {
        title: 'Welcome to Field Analytics',
        description: 'Analyze your data sources, find orphaned fields, and identify dependencies across your metadata.',
        connectionType: 'data sources'
      };
    }
  }, [hasSalesforce, hasHubSpot]);

  // Derive auth status for the selected data source.
  const selectedDsAuth = useMemo(() => {
    if (selectedDataSource?.auth_status) {
      return {
        connected: selectedDataSource.auth_status === 'active',
        status: selectedDataSource.auth_status,
      };
    }
    const dsId = selectedDataSource?.data_source_id;
    if (dsId && authByDataSource[dsId]) {
      return authByDataSource[dsId];
    }
    if (authByDataSource['__legacy_sf__']) {
      return authByDataSource['__legacy_sf__'];
    }
    return { connected: false, status: 'none' };
  }, [selectedDataSource, authByDataSource]);
  const isConnected = selectedDsAuth.connected;
  const authStatus = selectedDsAuth.status;

  // Filter objects by selected data source (used by sync config tab and editor metadata guard)
  const dataSourceFilteredObjects = useMemo(() => {
    if (!selectedDataSource || !selectedDataSource.data_source_id) {
      return objects;
    }
    return objects.filter(obj => obj.data_source_id === selectedDataSource.data_source_id);
  }, [objects, selectedDataSource]);

  // Builder gets ALL synced objects (state.analytics.objects, not syncConfig.objects which
  // is scoped to the currently selected data source). null only when nothing is synced.
  const builderObjects = objects && objects.length > 0 ? objects : null;

  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);

  // Reference library state
  const [referenceLibraryStatus, setReferenceLibraryStatus] = useState(null);
  const [isEnablingReferenceLibrary, setIsEnablingReferenceLibrary] = useState(false);
  const [referenceLibraryError, setReferenceLibraryError] = useState(null);
  const [referenceLibraryResult, setReferenceLibraryResult] = useState(null);

  // Track loading start time
  useEffect(() => {
    if (!initialLoadComplete && !loadingStartTime) {
      setLoadingStartTime(Date.now());
    }
    if (initialLoadComplete) {
      setLoadingStartTime(null);
      setLoadingTimedOut(false);
    }
  }, [initialLoadComplete, loadingStartTime]);

  // Timeout after 15 seconds of loading
  useEffect(() => {
    if (!loadingStartTime || initialLoadComplete) return;

    const timer = setTimeout(() => {
      if (!initialLoadComplete) {
        setLoadingTimedOut(true);
      }
    }, 15000);

    return () => clearTimeout(timer);
  }, [loadingStartTime, initialLoadComplete]);

  // Retry loading function
  const handleRetryLoad = useCallback(() => {
    setLoadingTimedOut(false);
    setLoadingStartTime(Date.now());
    dispatch(resetInitialLoad());
    const traceId = generateTraceId();
    dispatch(fetchFields({ traceId }));
    dispatch(fetchSyncConfigs({ traceId }));
    dispatch(fetchSyncStatus({ traceId }));
    dispatch(fetchFullSyncStatus());
  }, [dispatch]);

  // Fetch reference library status on mount
  useEffect(() => {
    if (activeTenant && initialLoadComplete) {
      analyticsApi.fetchReferenceLibraryStatus()
        .then(res => setReferenceLibraryStatus(res))
        .catch(err => console.error('Failed to fetch reference library status:', err));
    }
  }, [activeTenant, initialLoadComplete]);

  // Handle enabling reference library
  const handleEnableReferenceLibrary = async () => {
    setIsEnablingReferenceLibrary(true);
    setReferenceLibraryError(null);
    setReferenceLibraryResult(null);

    try {
      const res = await analyticsApi.enableReferenceLibrary();
      setReferenceLibraryResult(res);
      setReferenceLibraryStatus({ ...referenceLibraryStatus, enabled: true });

      const traceId = generateTraceId();
      dispatch(fetchFields({ traceId }));
      dispatch(fetchFullSyncStatus());
    } catch (err) {
      setReferenceLibraryError(err.response?.data?.message || err.message || 'Failed to enable reference library');
    } finally {
      setIsEnablingReferenceLibrary(false);
    }
  };

  // Build metadata for SOQL editor intellisense (all sources, not filtered by selection)
  const editorMetadata = useMemo(() => {
    if (!objects || objects.length === 0) return null;

    const objectsMap = {};
    for (const obj of objects) {
      objectsMap[obj.name] = {
        label: obj.label,
        fields: objectFields[obj.name] || [],
        sourceType: obj.source_type,
      };
    }

    const lookupRelationships = [];
    for (const obj of objects) {
      const fields = objectFields[obj.name] || [];
      for (const field of fields) {
        if (field.type === 'reference' && field.referenceTo) {
          lookupRelationships.push({
            sourceObject: obj.name,
            relationshipName: field.relationshipName || field.name.replace(/Id$/, ''),
            referenceTo: field.referenceTo,
          });
        }
      }
    }

    // Build per-data-source object maps for alias-prefixed completions
    const dataSourcesWithObjects = dataSources
      ? dataSources
          .filter(ds => ds.alias)
          .map(ds => {
            const dsObjects = {};
            for (const obj of objects) {
              if (obj.data_source_id === ds.data_source_id) {
                dsObjects[obj.name] = {
                  label: obj.label,
                  fields: objectFields[obj.name] || [],
                };
              }
            }
            return {
              id: ds.data_source_id,
              alias: ds.alias,
              name: ds.name,
              sourceType: ds.source_type,
              objects: dsObjects,
            };
          })
          .filter(ds => Object.keys(ds.objects).length > 0)
      : [];

    return {
      objects: objectsMap,
      lookupRelationships,
      dataSources: dataSourcesWithObjects.length > 0 ? dataSourcesWithObjects : undefined,
    };
  }, [objects, objectFields, dataSources]);

  useEffect(() => {
    if (activeTenant) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'page_load', { page: 'analytics' });
      dispatch(fetchFields({ traceId }));
      dispatch(fetchSyncConfigs({ traceId }));
      dispatch(fetchSyncStatus({ traceId }));
      dispatch(fetchFullSyncStatus());
      dispatch(fetchDataSources({ traceId }));
    }
  }, [dispatch, activeTenant]);

  useEffect(() => {
    if (selectedObject && !objectFields[selectedObject]) {
      dispatch(fetchFieldsByObject({ objectName: selectedObject }));
    }
  }, [dispatch, selectedObject, objectFields]);

  // Poll workflow status when sync is in progress
  useEffect(() => {
    if (!activeSyncWorkflow || activeSyncWorkflow.status !== 'started' && activeSyncWorkflow.status !== 'running') {
      return;
    }

    const pollInterval = setInterval(() => {
      dispatch(pollSyncWorkflowProgress({ workflowId: activeSyncWorkflow.workflowId }));
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [dispatch, activeSyncWorkflow]);

  // When workflow completes, refetch fields
  useEffect(() => {
    if (activeSyncWorkflow?.status === 'completed') {
      const traceId = generateTraceId();
      dispatch(fetchFields({ traceId }));
      setTimeout(() => {
        dispatch(clearActiveSyncWorkflow());
      }, 2000);
    }
  }, [dispatch, activeSyncWorkflow?.status]);

  const handleTriggerFirstSync = async () => {
    const traceId = generateTraceId();
    logUserAction(traceId, 'trigger_first_sync', {});
    await dispatch(triggerFieldSync({ traceId, dataSourceId: selectedDataSource?.data_source_id }));
  };

  // Determine if we should show the onboarding screen
  const showOnboarding = initialLoadComplete && dataSourceFilteredObjects.length === 0 && !activeSyncWorkflow;

  const tabLinkClasses = ({ isActive }) =>
    `pb-4 px-2 font-medium transition ${
      isActive
        ? 'text-primary-600 border-b-2 border-primary-600'
        : 'text-gray-500 hover:text-gray-700'
    }`;

  // Compute loading steps/errors for loading state UI
  const { loadingSteps, loadingErrors } = useMemo(() => {
    const steps = [];
    const errs = [];

    if (isAuthLoading || !activeTenant) {
      steps.push('Authenticating');
    } else {
      if (initialLoadStatus.syncStatus === 'loading') {
        steps.push('Checking data source connection');
      } else if (initialLoadStatus.syncStatus === 'error') {
        errs.push({ step: 'Data source connection', error: initialLoadStatus.syncStatusError });
      }

      if (initialLoadStatus.fields === 'loading') {
        steps.push('Loading field metadata');
      } else if (initialLoadStatus.fields === 'error') {
        errs.push({ step: 'Field metadata', error: initialLoadStatus.fieldsError });
      }

      if (initialLoadStatus.fullSyncStatus === 'loading') {
        steps.push('Loading sync configuration');
      } else if (initialLoadStatus.fullSyncStatus === 'error') {
        errs.push({ step: 'Sync configuration', error: initialLoadStatus.fullSyncStatusError });
      }

      if (steps.length === 0 && errs.length === 0) {
        steps.push('Starting...');
      }
    }

    return { loadingSteps: steps, loadingErrors: errs };
  }, [isAuthLoading, activeTenant, initialLoadStatus]);

  // Show loading state before initial load completes
  if (!initialLoadComplete) {
    if (loadingTimedOut) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="bg-amber-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Taking longer than expected</h2>
            <p className="text-gray-600 mb-4">
              The analytics service is slow to respond. This could be due to network issues or the service being busy.
            </p>

            {loadingSteps.length > 0 && (
              <div className="mb-4 text-sm text-gray-500">
                <p className="mb-2">Still waiting for:</p>
                <ul className="space-y-1">
                  {loadingSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ul>
              </div>
            )}

            {loadingErrors.length > 0 && (
              <div className="mb-4 text-left bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-medium text-red-800 mb-2">Errors encountered:</h3>
                <ul className="space-y-2 text-sm text-red-700">
                  {loadingErrors.map((err, i) => (
                    <li key={i}>
                      <span className="font-medium">{err.step}:</span> {sanitizeDisplayError(err.error)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={handleRetryLoad}
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">Loading analytics...</p>

          {loadingSteps.length > 0 && (
            <div className="mt-4 text-sm text-gray-500">
              <ul className="space-y-1">
                {loadingSteps.map((step, i) => (
                  <li key={i} className="flex items-center justify-center gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                    {step}...
                  </li>
                ))}
              </ul>
            </div>
          )}

          {loadingErrors.length > 0 && (
            <div className="mt-4 text-left bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-800 mb-2">Loading errors:</h3>
              <ul className="space-y-2 text-sm text-red-700">
                {loadingErrors.map((err, i) => (
                  <li key={i}>
                    <span className="font-medium">{err.step}:</span> {sanitizeDisplayError(err.error, 'Unknown error')}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleRetryLoad}
                className="mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Retry loading
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show sync in progress state
  if (activeSyncWorkflow) {
    const isRunning = activeSyncWorkflow.status === 'started' || activeSyncWorkflow.status === 'running';
    const isFailed = activeSyncWorkflow.status === 'failed';
    const isCompleted = activeSyncWorkflow.status === 'completed';

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          {isRunning && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Syncing Your {splashPageText.connectionType}</h2>
              <p className="text-gray-600 mb-4">
                {activeSyncWorkflow.current_step || "We're analyzing your field definitions and metadata."}
              </p>
              {activeSyncWorkflow.objects_total > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Progress</span>
                    <span>{activeSyncWorkflow.objects_processed || 0} / {activeSyncWorkflow.objects_total} objects</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${activeSyncWorkflow.progress_percent || 0}%` }}
                    ></div>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                <span>{activeSyncWorkflow.current_object ? `Processing: ${activeSyncWorkflow.current_object}` : 'Starting...'}</span>
              </div>
            </>
          )}
          {isCompleted && (
            <>
              <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Sync Complete!</h2>
              <p className="text-gray-600 mb-4">
                Your field metadata has been synced successfully. Loading results...
              </p>
            </>
          )}
          {isFailed && (
            <>
              <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Sync Failed</h2>
              <p className="text-gray-600 mb-4">
                {sanitizeDisplayError(activeSyncWorkflow.error, `The sync workflow encountered an error. Please check that ${splashPageText.connectionType} is connected and try again.`)}
              </p>
              <button
                onClick={() => dispatch(clearActiveSyncWorkflow())}
                className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition font-medium"
              >
                Try Again
              </button>
            </>
          )}
          <div className="bg-gray-100 rounded-lg p-3 text-sm text-gray-500 font-mono">
            Workflow: {activeSyncWorkflow.workflowId}
          </div>
        </div>
      </div>
    );
  }

  // Show onboarding screen for first-time users
  if (showOnboarding) {
    const needsReauth = authStatus === 'needs_reauth';
    const notConnected = authStatus === 'none' || !isConnected;

    const handleReconnect = () => {
      if (selectedDataSource?.source_type === 'hubspot') {
        redirectToHubSpotReauth('/analytics', activeTenant?.tenant_id);
      } else {
        redirectToSalesforceReauth('/analytics');
      }
    };

    const selectedSourceName = selectedDataSource?.name || 'your data source';
    const selectedSourceType = selectedDataSource ?
      getDataSourceConfig(selectedDataSource.source_type).displayName : 'data source';

    return (
      <div>
        {/* Always show data source selector */}
        <DataSourceSelector showAlways={true} />

        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-lg">
            <div className="bg-primary-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{splashPageText.title}</h1>
            <p className="text-gray-600 mb-2 text-lg">
              {splashPageText.description}
            </p>
            {selectedDataSource && (
              <p className="text-gray-500 mb-6 text-sm">
                Ready to sync: <span className="font-medium text-gray-700">{selectedSourceName}</span> ({selectedSourceType})
              </p>
            )}

          {needsReauth ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-amber-800 mb-1">{splashPageText.connectionType} Connection Expired</h3>
                    {isAdmin ? (
                      <p className="text-amber-700 text-sm">
                        Your {splashPageText.connectionType} authorization has expired and needs to be refreshed.
                        Click below to reconnect your {splashPageText.connectionType} account.
                      </p>
                    ) : (
                      <p className="text-amber-700 text-sm">
                        The {splashPageText.connectionType} connection for this organization has expired.
                        Please contact your admin to reconnect {splashPageText.connectionType}.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {isAdmin ? (
                <button
                  onClick={handleReconnect}
                  className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition font-medium text-lg"
                >
                  Reconnect {splashPageText.connectionType}
                </button>
              ) : (
                <div className="text-gray-500 text-sm">
                  Only tenant administrators can reconnect the {splashPageText.connectionType} integration.
                </div>
              )}
            </>
          ) : notConnected ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-amber-800 mb-1">Connect {splashPageText.connectionType} First</h3>
                    {isAdmin ? (
                      <p className="text-amber-700 text-sm">
                        To analyze your data, you need to connect your {splashPageText.connectionType} account.
                        Click below to connect via OAuth.
                      </p>
                    ) : (
                      <p className="text-amber-700 text-sm">
                        To analyze your data, a {splashPageText.connectionType} connection is required.
                        Please contact your admin to set up the {splashPageText.connectionType} integration.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {isAdmin ? (
                <button
                  onClick={handleReconnect}
                  className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition font-medium text-lg"
                >
                  Connect {splashPageText.connectionType}
                </button>
              ) : (
                <div className="text-gray-500 text-sm">
                  Only tenant administrators can connect the {splashPageText.connectionType} integration.
                </div>
              )}
            </>
          ) : (
            <>
              <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-3">What we'll analyze:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>All custom and standard fields across your objects</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Field types, relationships, and metadata</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Orphaned fields not used in any code or config</span>
                  </li>
                </ul>
              </div>
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-left">
                  {sanitizeDisplayError(error)}
                </div>
              )}
              <button
                onClick={handleTriggerFirstSync}
                disabled={isTriggeringSync}
                className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTriggeringSync ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Starting sync...
                  </span>
                ) : (
                  'Start First Sync'
                )}
              </button>
              <p className="mt-4 text-sm text-gray-500">
                This will take 1-3 minutes depending on your org size
              </p>
            </>
          )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Data Source Selector - Always visible */}
      <DataSourceSelector showAlways={true} />

      <div className="px-6 py-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Field Analytics</h1>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {sanitizeDisplayError(error)}
          </div>
        )}

        {/* Reference Library Enable Banner */}
        {referenceLibraryStatus && !referenceLibraryStatus.enabled && isConnected && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="reference-library-banner">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 rounded-full p-2 flex-shrink-0">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">Get Started with Pre-Built Analytics</h3>
                <p className="text-blue-700 text-sm mb-3">
                  Enable the Reference Library to instantly get {referenceLibraryStatus.catalog_item_count || 'curated'} pre-built
                  views for pipeline analysis, deal tracking, account insights, and more.
                  Required Salesforce objects will be automatically synced.
                </p>

                {referenceLibraryError && (
                  <div className="mb-3 p-2 bg-red-100 border border-red-200 rounded text-red-700 text-sm">
                    {sanitizeDisplayError(referenceLibraryError)}
                  </div>
                )}

                {referenceLibraryResult && (
                  <div className="mb-3 p-2 bg-green-100 border border-green-200 rounded text-green-700 text-sm" data-testid="enable-success">
                    Enabled! Installed {referenceLibraryResult.installed_counts?.views || 0} views.
                    {referenceLibraryResult.sync_configured?.length > 0 && (
                      <> Syncing: {referenceLibraryResult.sync_configured.join(', ')}</>
                    )}
                  </div>
                )}

                <button
                  onClick={handleEnableReferenceLibrary}
                  disabled={isEnablingReferenceLibrary}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="enable-reference-library"
                >
                  {isEnablingReferenceLibrary ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Enabling...
                    </>
                  ) : (
                    'Enable Reference Library'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sub-tab navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4">
            <NavLink
              to="/analytics/query"
              className={tabLinkClasses}
              data-testid="analytics-tab-query"
            >
              Query
            </NavLink>
            <NavLink
              to="/analytics/views"
              className={tabLinkClasses}
              data-testid="analytics-tab-views"
            >
              Views
            </NavLink>
            <NavLink
              to="/analytics/visualizations"
              className={tabLinkClasses}
              data-testid="analytics-tab-visualizations"
            >
              Visualizations
            </NavLink>
            <NavLink
              to="/analytics/dashboards"
              className={tabLinkClasses}
              data-testid="analytics-tab-dashboards"
            >
              Dashboards
            </NavLink>
            <NavLink
              to="/analytics/dependencies"
              className={tabLinkClasses}
              data-testid="analytics-tab-dependencies"
            >
              Dependency Graph
            </NavLink>
          </nav>
        </div>

        {/* Sub-tab content via nested routes */}
        <Routes>
          <Route index element={<Navigate to="query" replace />} />
          <Route path="query" element={<QueryContent editorMetadata={editorMetadata} builderObjects={builderObjects} />} />
          <Route path="views" element={<ViewsTab metadata={null} />} />
          <Route path="visualizations" element={<VisualizationsTab />} />
          <Route path="dashboards" element={<DashboardsTab />} />
          <Route
            path="dependencies"
            element={<DependencyGraphTab />}
          />
          <Route path="*" element={<Navigate to="query" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default AnalyticsPage;
